// @flow
import { EmailOutlined, Settings } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import Button from "@mui/material/Button";
import React, { useCallback, useMemo, useState } from 'react';
import AddPlusIcon from "../../../../assets/images/icons/buttons/addPlusIcon";
import TrashIcon from "../../../../assets/images/icons/buttons/trashIcon";
import ConfirmDialog from "../../../../components/app/dialogs/confirmDialog/confirmDialog";
import {
	CustomPagination
} from "../../../../components/grids/dataGridCustom/pagination/customPaginations/customPagination";
import { showOption } from "../../../../helpers/helpers";
import { muiGridLocales } from "../../../../helpers/muiGridLocales";
import { normalizeGreek } from "../../../../helpers/normalizeGreek";
import { useCoorganizations } from "../../../../hooks/coorganizations/useCoorganizations";
import { Alert, ListItem, Tooltip } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import useUserData from "../../../../hooks/user/useUserData";
import { EXHIBITION, GENERAL_SETTINGS } from "../../../../locales/components/namespaces";
import { DataGridPremium, GridActionsCellItem, GridToolbarContainer } from "@mui/x-data-grid-premium";
import Box from "@mui/material/Box";
import InvitationMail from "../../../exhibitionSettings/pages/invitationMail/invitationMail";
import SelectExhibitions from "./selectExhibitions";
import Breadcrumb from "../../../../components/general/breadcrumb/breadcrumb";
import HomeIcon from "@mui/icons-material/Home";
import { getRouteUrl } from "../../../../helpers/getRouteUrl";
import {
	ROUTE_PAGE_EXHIBITION_SETTINGS,
	ROUTE_PAGE_HOME,
} from "../../../../routers/routes";
import List from "@mui/material/List";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import { Permissions } from "../../../../helpers/constants";
import { withPermissionHOC } from "../../../../modules/permission/components/permissionHOC/permissionHOC";

const CustomToolbar = (props: Props) => {
	const { onAdd } = props
	const { t } = useTranslation(GENERAL_SETTINGS)
	return (
		<GridToolbarContainer>
			<Button
				color={"highlight"}
				size={"small"}
				startIcon={<AddPlusIcon/>}
				onClick={onAdd}
				sx={{ textTransform: 'uppercase' }}
			>
				{normalizeGreek(t('Add'))}
			</Button>
		</GridToolbarContainer>
	);
};

const Coorganizations = (props) => {
	const { t } = useTranslation(GENERAL_SETTINGS)
	const { language } = useUserData()
	
	const {
		coorganizations,
		loading,
		getCoorganizations,
		getExhibitions,
		exhibitions,
		exhibitionsLoading,
		deleteCoorganization,
		confirmDeleteCoorganization,
		setConfirmDeleteCoorganization
	} = useCoorganizations()
	
	const [openForm, setOpenForm] = useState({
		open: false,
		coorganizationId: null,
		exhibitionsIds: [],
		mainExhibitionId: null,
		descriptionEn: "",
		descriptionEl: "",
	})
	const [emailSettings, setEmailSettings] = useState({
		open: false,
		mainExhibitionId: null
	})
	
	const onAdd = useCallback(() => {
		setOpenForm({
			open: true,
			coorganizationId: null,
			exhibitionsIds: [],
			mainExhibitionId: null,
		})
		getExhibitions()
	}, [getExhibitions])
	
	const onEdit = useCallback((row) => {
		setOpenForm({
			open: true,
			coorganizationId: row.id,
			exhibitionsIds: row.exhibitionsIds?.split(','),
			mainExhibitionId: row.mainExhibitionId,
			descriptionEn: row.description.en,
			descriptionEl: row.description.el,
		})
		getExhibitions()
	}, [getExhibitions])
	
	const onDelete = useCallback((id) => {
		setConfirmDeleteCoorganization({
			open: true,
			loading: false,
			id: id
		})
	}, [setConfirmDeleteCoorganization])
	
	const columns = useMemo(() => [
		{
			field: 'actions',
			headerName: t('Actions'),
			type: 'actions',
			width: 140,
			getActions: (params) => [
				<GridActionsCellItem
					onClick={() => onEdit(params.row)}
					icon={<EditIcon/>}
					label={t("Edit Coorganization")}
				/>,
				<GridActionsCellItem
					onClick={() => onDelete(params.row.id)}
					icon={<TrashIcon/>}
					label={t("Remove Coorganization")}
					color={"error"}
				/>,
				<GridActionsCellItem
					onClick={() => {
						setEmailSettings({
							open: true,
							mainExhibitionId: params.row.mainExhibitionId !== "" ? params.row.mainExhibitionId : null,
						})
					}}
					icon={<EmailOutlined/>}
					label={t("Email Settings")}
					color={"info"}
				/>,
			],
		},
		{
			field: 'description',
			headerName: t('Description'),
			minWidth: '200px',
			flex: 1,
			renderCell: ({ row }) => (showOption(language)(row.description))
		},
		{
			field: 'mainExhibition',
			headerName: t('Main Exhibition'),
			minWidth: '200px',
			flex: 1,
			renderCell: ({ row }) => {
				return (
					<Box>
						<Tooltip title={t('Main Exhibition Settings')}>
							<a
								href={getRouteUrl(ROUTE_PAGE_EXHIBITION_SETTINGS, { id: row.mainExhibitionId })}
								target={'_blank'}
								rel="noopener noreferrer"
							>
								<Box sx={{ display: 'flex', alignItems: 'center' }}>
									{showOption(language)(row.mainExhibition)}
									{Boolean(row.mainExhibitionId) && (<Settings fontSize={'small'} color={'info'}/>)}
								</Box>
							</a>
						</Tooltip>
					</Box>
				
				)
			}
		},
	], [language, onDelete, onEdit, t])
	
	const closeForm = useCallback(() => {
		setOpenForm({
			open: false,
			coorganizationId: null,
			exhibitionsIds: [],
			mainExhibitionId: null,
			descriptionEn: "",
			descriptionEl: "",
		})
	}, [])
	return (
		<React.Fragment>
			<Breadcrumb>
				<Breadcrumb.Link iconComponent={HomeIcon} to={getRouteUrl(ROUTE_PAGE_HOME)}>
					{t('Home')}
				</Breadcrumb.Link>
				<Breadcrumb.Item>
					{t('Coorganizations')}
				</Breadcrumb.Item>
			</Breadcrumb>
			<Paper sx={{ mb: 1 }} variant={"outlined"}>
				<List>
					<ListItem component={"div"}>
						<ListItemText
							primary={
								<Typography component={"div"} variant={"body1"}>
									<Trans
										i18nKey={'create-coorganization'}
										ns={EXHIBITION}
									>
										On this page you can set a new <strong>Coorganization</strong> and connect it
										with exhibitions.
									</Trans>
								</Typography>
							}
						/>
					</ListItem>
				</List>
			</Paper>
			<Box>
				<DataGridPremium
					loading={loading}
					sx={{ bgcolor: 'background.paper' }}
					initialState={{
						density: "standard",
					}}
					localeText={muiGridLocales(language)}
					rows={coorganizations}
					columns={columns}
					pagination
					slots={{
						toolbar: CustomToolbar,
						pagination: CustomPagination
					}}
					slotProps={{
						toolbar: {
							onAdd: onAdd
						}
					}}
				/>
				<ConfirmDialog
					maxWidth={"md"}
					open={Boolean(openForm?.open)}
					handleDialog={closeForm}
					title={openForm?.coorganizationId ? t('Update Coorganization') : t('Create Coorganization')}
					otherDialogProps={{ disableEscapeKeyDown: true, fullWidth: true }}
					yesAction={false}
					noAction={false}
				>
					<SelectExhibitions
						coorganizationId={openForm?.coorganizationId}
						coorganizationExhibitions={openForm?.exhibitionsIds}
						mainExhibitionId={openForm?.mainExhibitionId}
						descriptionEl={openForm?.descriptionEl}
						descriptionEn={openForm?.descriptionEn}
						exhibitions={exhibitions}
						exhibitionsLoading={exhibitionsLoading}
						onSuccess={() => {
							getCoorganizations()
							closeForm()
						}}
					/>
				</ConfirmDialog>
				<ConfirmDialog
					loading={confirmDeleteCoorganization?.loading}
					withLoadingButton
					onYes={() => deleteCoorganization(confirmDeleteCoorganization?.id)}
					onNo={() => {
						setConfirmDeleteCoorganization({
							open: false,
							loading: false,
							id: ''
						})
					}}
					handleDialog={(newValue) => {
						setConfirmDeleteCoorganization({
							open: newValue,
							loading: false,
							id: ''
						})
					}}
					title={t('Confirm Delete')}
					open={confirmDeleteCoorganization?.open}
					hasBtnIcons={false}
				>
					{t('This action is permanent. Press OK if you want to proceed.')}
				</ConfirmDialog>
				<ConfirmDialog
					maxWidth={"lg"}
					open={Boolean(emailSettings?.open)}
					handleDialog={() => {
						setEmailSettings({
							open: false,
							mainExhibitionId: null
						})
					}}
					title={t('Coorganization Email/SMS Settings')}
					otherDialogProps={{ disableEscapeKeyDown: true, fullWidth: true }}
					yesAction={false}
					noAction={false}
				>
					{emailSettings?.mainExhibitionId ? (
						<InvitationMail
							exhibitionId={emailSettings?.mainExhibitionId}
							isCoorganization={true}
						/>
					) : (
						<Alert severity="warning" sx={{ fontSize: 14 }}>
							{t('Declare main exhibition for coorganization, to declare email/sms settings.')}
						</Alert>
					)}
				</ConfirmDialog>
			</Box>
		</React.Fragment>
	);
};

export default withPermissionHOC(Coorganizations, [Permissions.BeOrganizer])